/**
 * Chat Input - Claude Desktop Style
 *
 * Clean rounded input with:
 * - "Reply..." placeholder
 * - Attachment buttons (disabled, future)
 * - Model selector
 * - Send button with arrow icon
 */

import * as React from 'react'
import { ArrowUp, Square, Plus, Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { ModelSelector, type AIModel } from './model-selector'

interface ChatInputProps {
  value: string
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  onSubmit: (e: React.FormEvent) => void
  onStop?: () => void
  isLoading?: boolean
  placeholder?: string
  disabled?: boolean
  // Model selector props
  models?: AIModel[]
  selectedModel?: string | null
  onSelectModel?: (modelId: string) => void
}

export function ChatInput({
  value,
  onChange,
  onSubmit,
  onStop,
  isLoading = false,
  placeholder = 'Reply...',
  disabled = false,
  models = [],
  selectedModel = null,
  onSelectModel,
}: ChatInputProps) {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null)
  const wasLoadingRef = React.useRef(false)

  // Handle Ctrl+Enter (submit), Enter/Shift+Enter (new line)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      if (value.trim() && !isLoading && !disabled) {
        onSubmit(e)
      }
    }
    // Enter and Shift+Enter = new line (default behavior, no preventDefault)
  }

  // Auto-resize textarea
  React.useEffect(() => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = 'auto'
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`
    }
  }, [value])

  // Auto-focus after loading completes
  React.useEffect(() => {
    if (wasLoadingRef.current && !isLoading) {
      // Loading just finished, focus the textarea
      textareaRef.current?.focus()
    }
    wasLoadingRef.current = isLoading
  }, [isLoading])

  const hasModels = models.length > 0

  return (
    <form onSubmit={onSubmit} className="relative">
      <div className="relative flex flex-col rounded-2xl border border-border bg-card shadow-sm focus-within:ring-2 focus-within:ring-ring/20 focus-within:border-ring/50 transition-all">
        {/* Textarea */}
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={onChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={isLoading || disabled}
          rows={1}
          className={cn(
            'flex-1 resize-none border-0 bg-transparent px-4 pt-4 pb-2',
            'focus-visible:ring-0 focus-visible:ring-offset-0',
            'min-h-[52px] max-h-[200px]',
            '!text-base md:!text-[15px] placeholder:text-muted-foreground/60'
          )}
        />

        {/* Bottom toolbar - Claude style */}
        <div className="flex items-center justify-between px-2 py-2">
          {/* Left side - attachment buttons (future) */}
          <div className="flex items-center gap-0.5">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-foreground rounded-lg"
              disabled
              title="Attach file (coming soon)"
            >
              <Plus className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-foreground rounded-lg"
              disabled
              title="Recent (coming soon)"
            >
              <Clock className="h-4 w-4" />
            </Button>
          </div>

          {/* Right side - model selector + send */}
          <div className="flex items-center gap-1.5">
            {hasModels && onSelectModel && (
              <ModelSelector
                models={models}
                selectedModel={selectedModel}
                onSelectModel={onSelectModel}
                disabled={isLoading || disabled}
              />
            )}
            {isLoading ? (
              <Button
                type="button"
                size="icon"
                onClick={onStop}
                className={cn(
                  'h-8 w-8 rounded-lg',
                  'bg-destructive text-destructive-foreground hover:bg-destructive/90',
                  'transition-all'
                )}
                title="Stop generating"
              >
                <Square className="h-3.5 w-3.5 fill-current" />
              </Button>
            ) : (
              <Button
                type="submit"
                size="icon"
                disabled={!value.trim() || disabled}
                className={cn(
                  'h-8 w-8 rounded-lg',
                  'bg-primary text-primary-foreground hover:bg-primary/90',
                  'disabled:opacity-30 disabled:bg-muted disabled:text-muted-foreground',
                  'transition-all'
                )}
              >
                <ArrowUp className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </form>
  )
}
